// ==============================
// Imports
// ==============================

// Local libraries
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';

// Local Components
import { apiDelete, apiGet, apiPut } from '../utils/apiClient';
import {
  CoachesListResponse,
  Coach,
  SuccessResponse,
} from '../types/CoachTable.type';
import { notifyAPIClient } from '../utils/toast';
import { exportRowsToXlsx } from '../utils/xlsxExport';

export const useCoachController = () => {
  // States
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [openCoachInfoDialog, setOpenCoachInfoDialog] = useState(false);
  const [openDeleteCoachDialog, setOpenDeleteCoachDialog] = useState(false);
  const [openCreateCoachDialog, setOpenCreateCoachDialog] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Last sync time for coaches
  const [lastSyncTime, setLastSyncTime] = useState<dayjs.Dayjs | null>(null);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // Networking
  const fetchCoaches = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiGet<CoachesListResponse>(
        `/auth/coaches?page=${page}&limit=${limit}&search=${searchTerm}`,
      );
      setCoaches(res.data.coaches);
      setTotalItems(res.data.pagination.total);
      setLastSyncTime(dayjs());
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm]);

  // Hooks
  useEffect(() => {
    fetchCoaches();
  }, [page, limit]);

  // ==============================
  // Update: Fetch coaches every 3 seconds
  // ==============================
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCoaches(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchCoaches]);

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // ==============================
  // Clear all filters
  // ==============================
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCoach(null);
    setPage(1);
    setLimit(10);
  };

  const handleCloseCoachInfoDialog = () => {
    setSelectedCoach(null);
    setOpenCoachInfoDialog(false);
  };

  const handleDeleteCoach = async (coach: Coach) => {
    try {
      const res = await apiDelete<SuccessResponse>(`/auth/coaches/${coach.id}`);
      notifyAPIClient(res.message);
      setSelectedCoach(null);
      fetchCoaches();
    } catch (error) {
      console.error('Failed to delete coach:', error);
    }
  };

  const handleUpdateCoach = async (coach: Coach) => {
    try {
      const res = await apiPut<SuccessResponse, Partial<Coach>>(
        `/auth/coaches/${coach.id}`,
        coach,
      );
      notifyAPIClient(res.message);
      setSelectedCoach(null);
      fetchCoaches();
    } catch (error) {
      console.error('Failed to update coach:', error);
    }
  };

  const handleDownloadCoaches = async () => {
    if (totalItems === 0) return;

    setDownloadLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: totalItems.toString(),
        search: searchTerm,
      });
      const response = await apiGet<CoachesListResponse>(
        `/auth/coaches?${params.toString()}`,
      );

      const rows = response.data.coaches.map((coach) => ({
        ID: coach.id,
        Name: coach.name,
        Email: coach.email,
        'Created At': new Date(coach.createdAt).toLocaleDateString(),
      }));

      exportRowsToXlsx(
        rows,
        `coaches_${dayjs().format('YYYY-MM-DD')}.xlsx`,
        Date.now().toString(),
      );
    } catch (error) {
      console.error('Failed to download coaches:', error);
      notifyAPIClient('Failed to download coaches. Please try again later.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const emptyRows =
    page * limit >= totalItems ? Math.max(0, limit - coaches.length) : 0;

  return {
    coaches,
    page,
    limit,
    totalItems,
    searchTerm,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    clearFilters,
    fetchCoaches,
    handleDownloadCoaches,
    downloadLoading,
    loading,
    setPage,

    // Coach Info Dialog
    openCoachInfoDialog,
    setOpenCoachInfoDialog,
    selectedCoach,
    setSelectedCoach,
    handleCloseCoachInfoDialog,

    // Delete Coach Dialog
    handleDeleteCoach,
    openDeleteCoachDialog,
    setOpenDeleteCoachDialog,

    // Update Coach Dialog
    handleUpdateCoach,

    // Create Coach Dialog
    openCreateCoachDialog,
    setOpenCreateCoachDialog,

    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
  };
};
